import {useDebounceEffect} from '@/utilities/useDebounceEffect';
import React, {useRef, useState} from 'react';
import ReactCrop, {centerCrop, Crop, makeAspectCrop, PixelCrop} from 'react-image-crop';
import {imageCropCanvasPreview} from '../user/_components/ImageCrop';
import PButton from './PButton';

import 'react-image-crop/dist/ReactCrop.css';

interface BannerImageCropProps {
    imgSrc: string;
    aspect?: number;
    onSave: (crop: {x: number; y: number; width: number; height: number}) => void;
    onCancel: () => void;
}

export default function BannerImageCrop({imgSrc, aspect, onSave, onCancel}: BannerImageCropProps) {
    const previewCanvasRef = useRef<HTMLCanvasElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const [crop, setCrop] = useState<Crop>();
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
    const [completedPercentCrop, setCompletedPercentCrop] = useState<Crop>();

    function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
        const {width, height} = e.currentTarget;
        if (aspect) {
            setCrop(centerCrop(
                makeAspectCrop({unit: '%', width: 90}, aspect, width, height),
                width,
                height,
            ));
        } else {
            setCrop(centerCrop(
                makeAspectCrop({unit: '%', width: 90}, 16 / 5, width, height),
                width,
                height,
            ));
        }
    }

    useDebounceEffect(
        async () => {
            if (completedCrop?.width && completedCrop?.height && imgRef.current && previewCanvasRef.current) {
                imageCropCanvasPreview(imgRef.current, previewCanvasRef.current, completedCrop);
            }
        },
        100,
        [completedCrop],
    );

    return (
        <div className="space-y-4">
            <p className="text-sm text-default-500">
                Drag to reposition, drag the edges to resize the crop area.
            </p>

            <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c, percentCrop) => {
                    setCompletedCrop(c);
                    setCompletedPercentCrop(percentCrop);
                }}
                aspect={aspect}
                minHeight={30}
            >
                <img
                    ref={imgRef}
                    alt="Crop banner"
                    src={imgSrc}
                    style={{display: 'block', maxHeight: '500px', maxWidth: '100%'}}
                    onLoad={onImageLoad}
                />
            </ReactCrop>

            {!!completedCrop && (
                <div>
                    <p className="text-xs text-default-400 mb-2">Preview:</p>
                    <canvas
                        ref={previewCanvasRef}
                        className="rounded-lg border border-default-200"
                        style={{
                            width: '100%',
                            height: 'auto',
                            maxHeight: '200px',
                            objectFit: 'contain',
                        }}
                    />
                </div>
            )}

            <div className="flex gap-2 justify-end">
                <PButton variant="light" onPress={onCancel}>
                    Cancel
                </PButton>
                <PButton
                    color="primary"
                    onPress={() => {
                        const saveCrop = completedPercentCrop || crop;
                        if (saveCrop) {
                            onSave({
                                x: saveCrop.x as number,
                                y: saveCrop.y as number,
                                width: saveCrop.width as number,
                                height: saveCrop.height as number,
                            });
                        }
                    }}
                >
                    Save Crop
                </PButton>
            </div>
        </div>
    );
}
